import { redirect } from "@tanstack/react-router";
import { authClient } from "./auth-client";

/**
 * Auth verdict for route guards.
 * - `authenticated`   — a valid session with a user.
 * - `unauthenticated` — definitively signed out (no session, or a 401).
 * - `unknown`         — we could not determine (network error / 429 / 5xx).
 *
 * Guards must **fail open** on `unknown`: never redirect a possibly-authenticated
 * user to /auth because of a transient error, or a rate-limited `get-session`
 * turns into an /auth ↔ /dashboard redirect loop.
 */
export type AuthVerdict = "authenticated" | "unauthenticated" | "unknown";

type SessionResult = {
  data?: { user?: unknown } | null;
  error?: { status?: number } | null;
} | null;

// Short-TTL cache + in-flight de-duplication. The root `beforeLoad`, the
// per-route guards, preloads and `this.load()` reloads all funnel through here,
// and router-core runs `beforeLoad` on *every* navigation/search-change/preload
// with no staleness gate — so without this, one user action fans out into many
// `get-session` requests. With it, a burst collapses to a single network call.
const SESSION_TTL_MS = 5_000;
let cached: { result: SessionResult; at: number } | null = null;
let inflight: Promise<SessionResult> | null = null;
// Bumped on invalidation so a request that was already in flight when the cache
// was invalidated (sign-in/sign-out) cannot repopulate `cached` with a stale
// verdict when it settles — that could pin e.g. "unauthenticated" for a full
// TTL right after a successful sign-in.
let generation = 0;

/** Drop the cached session — call right after sign-in / sign-out so the next
 * guard re-reads fresh instead of trusting a stale verdict. */
export function invalidateAuthCache(): void {
  generation += 1;
  cached = null;
  inflight = null;
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : 0;
}

async function loadSession(): Promise<SessionResult> {
  if (cached && now() - cached.at < SESSION_TTL_MS) {
    return cached.result;
  }
  if (inflight) return inflight;

  const requestGeneration = generation;
  const request = (async (): Promise<SessionResult> => {
    try {
      return (await authClient.getSession()) as SessionResult;
    } catch (error) {
      // Network/abort/etc. — we cannot tell; surface as "unknown" (status 0).
      console.error("Error checking authentication:", error);
      return { error: { status: 0 } };
    }
  })()
    .then((result) => {
      // Only cache when no invalidation happened while this request was in
      // flight; its awaiters still get the result, but later guards re-fetch.
      if (requestGeneration === generation) {
        cached = { result, at: now() };
      }
      return result;
    })
    .finally(() => {
      // Same guard: an invalidated request must not clear a newer request's
      // in-flight registration.
      if (requestGeneration === generation) {
        inflight = null;
      }
    });

  inflight = request;
  return request;
}

/**
 * Resolve the auth verdict from the (cached) session. Fails open: any non-401
 * error is reported as `unknown` rather than `unauthenticated`.
 */
export async function getAuthVerdict(): Promise<AuthVerdict> {
  const result = await loadSession();
  if (result?.data?.user) return "authenticated";
  const status = result?.error?.status;
  if (status != null && status !== 401) return "unknown";
  return "unauthenticated";
}

/**
 * Boolean convenience wrapper. NOTE: treats `unknown` as not-authenticated, so
 * use `getAuthVerdict()` directly in redirecting guards to preserve fail-open.
 */
export async function checkAuthentication(): Promise<boolean> {
  return (await getAuthVerdict()) === "authenticated";
}

/**
 * Route guard for protected routes. Redirects to /auth only when definitively
 * unauthenticated; passes through on `unknown` (fail open).
 */
export async function requireAuth() {
  if ((await getAuthVerdict()) === "unauthenticated") {
    throw redirect({
      to: "/auth",
      search: {
        redirect: window.location.pathname,
      },
    });
  }
}

/**
 * Route guard for guest-only routes (like login/signup).
 * Redirects to /dashboard if user is already authenticated.
 */
export async function requireGuest() {
  if ((await getAuthVerdict()) === "authenticated") {
    throw redirect({
      to: "/dashboard",
    });
  }
}

/**
 * Route guard for admin-only routes.
 * Redirects to /dashboard if user is not an admin or owner.
 */
export async function requireAdmin() {
  const verdict = await getAuthVerdict();

  if (verdict === "unauthenticated") {
    throw redirect({
      to: "/auth",
      search: {
        redirect: window.location.pathname,
      },
    });
  }

  // Could not determine — fail open and let the page's own queries surface it.
  if (verdict === "unknown") return;

  const user = await getCurrentUser();
  const role = (user as { role?: string } | null)?.role;
  if (role !== "admin" && role !== "owner") {
    throw redirect({
      to: "/dashboard",
    });
  }
}

/**
 * Get current user from the (cached) session. Returns null if not authenticated.
 */
export async function getCurrentUser() {
  const result = await loadSession();
  return (result?.data?.user as { role?: string } | null) ?? null;
}
